/*
 * Painting a mask on the analysis grid: the brush and the eraser of the Spore
 * analysis workbench and of the Culture analysis page, the threshold preview
 * and its "Apply to mask", and a history to undo with. Pure functions on
 * typed arrays (a w x h Uint8Array, 1 = in the mask), so Vitest runs them
 * without a canvas.
 *
 * Coordinates come in IMAGE pixels, as the canvases report them; `scale` is
 * image pixels per grid pixel (imageWidth / gridWidth), and a grid pixel is
 * painted when its centre lies within the brush.
 */

/** The distance from p to the segment a-b. */
export function segmentDistance(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (!len2) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

/**
 * Paint (value 1) or erase (value 0) the brush's path from a to b, `width`
 * image pixels across, into `mask` in place. Returns the grid box it touched,
 * [x0, y0, x1, y1] inclusive, or null.
 */
export function paintSegment(mask, w, h, scale, a, b, width, value) {
  const r = width / 2;
  if (!(r > 0)) return null;
  const gx0 = Math.max(0, Math.floor((Math.min(a[0], b[0]) - r) / scale));
  const gx1 = Math.min(w - 1, Math.ceil((Math.max(a[0], b[0]) + r) / scale));
  const gy0 = Math.max(0, Math.floor((Math.min(a[1], b[1]) - r) / scale));
  const gy1 = Math.min(h - 1, Math.ceil((Math.max(a[1], b[1]) + r) / scale));
  if (gx0 > gx1 || gy0 > gy1) return null;
  for (let gy = gy0; gy <= gy1; gy += 1) {
    for (let gx = gx0; gx <= gx1; gx += 1) {
      if (segmentDistance([(gx + 0.5) * scale, (gy + 0.5) * scale], a, b) <= r) mask[gy * w + gx] = value;
    }
  }
  return [gx0, gy0, gx1, gy1];
}

/** A whole stroke (image-pixel points) painted into a copy of `mask`. */
export function paintStroke(mask, w, h, scale, points, width, value) {
  const out = mask.slice();
  if (!points.length) return out;
  if (points.length === 1) paintSegment(out, w, h, scale, points[0], points[0], width, value);
  for (let i = 1; i < points.length; i += 1) paintSegment(out, w, h, scale, points[i - 1], points[i], width, value);
  return out;
}

/** Every pixel of `add` added to `mask` (a copy). */
export function union(mask, add) {
  const out = mask.slice();
  for (let i = 0; i < out.length; i += 1) if (add[i]) out[i] = 1;
  return out;
}

/** The pixels of `mask` that `cut` does not cover (a copy). */
export function subtract(mask, cut) {
  const out = mask.slice();
  for (let i = 0; i < out.length; i += 1) if (cut[i]) out[i] = 0;
  return out;
}

/** Are the two masks the same? */
export function sameMask(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if ((a[i] ? 1 : 0) !== (b[i] ? 1 : 0)) return false;
  return true;
}

export const HISTORY_LIMIT = 20;

/** The history after one more change: the masks before it, newest last, at most HISTORY_LIMIT. */
export const pushHistory = (history, previous) => [...history, previous].slice(-HISTORY_LIMIT);

/** Drop points closer than `min` (image pixels) to the previous kept one: a stroke as it is kept. */
export function thinPoints(points, min) {
  const out = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) >= min) out.push(p);
  }
  return out;
}
